import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ScrollSection } from '../components/ScrollSection';

function DepartmentsCenters() {
  const { letter } = useParams();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const numbers = ['#'];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    
    if (searchParam) {
      setSearchQuery(searchParam);
      handleSearchDirect(searchParam);
    } else if (letter) {
      fetchDepartmentsByLetter(letter);
    } else {
      fetchDepartments();
    }
  }, [letter]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/departments');
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentsByLetter = async (ltr) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/departments?letter=${ltr}`);
      setDepartments(response.data.departments || []);
      setShowSearchResults(false);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchDirect = async (query) => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      const response = await axios.get(`/api/departments?search=${encodeURIComponent(query)}`);
      setSearchResults(response.data.departments || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search') || searchQuery;
    
    if (!searchParam.trim()) return;

    try {
      const response = await axios.get(`/api/departments?search=${encodeURIComponent(searchParam)}`);
      setSearchResults(response.data.departments || []);
      setShowSearchResults(true);
      setSearchQuery(searchParam);
    } catch (error) {
      console.error('Error searching departments:', error);
    }
  };

  const handleLetterClick = (ltr) => {
    navigate(`/departments-centers/${ltr}`);
    setShowSearchResults(false);
    setSearchQuery('');
    window.scrollTo(0, 0);
  };

  // Group departments by first letter(s) for display
  const groupDepartments = (deptList) => {
    const grouped = {};
    deptList.forEach(dept => {
      const key = dept.name.substring(0, 2).toUpperCase();
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(dept);
    });
    return grouped;
  };

  const groupedDepartments = groupDepartments(departments);
  const groupedSearchResults = groupDepartments(searchResults);

  return (
    <>
      {/* Hero Section - Dark Blue Background */}
      <section className="tgh-departments-hero py-5" style={{ backgroundColor: '#003d82', color: '#fff' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb" style={{ backgroundColor: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item"><Link to="/" style={{ color: '#fff' }}>Home</Link></li>
              <li className="breadcrumb-item"><span style={{ color: '#fff' }}>Departments & Centers</span></li>
              {letter && <li className="breadcrumb-item active" style={{ color: '#fff' }}>Begins with '{letter}'</li>}
            </ol>
          </nav>

          <h1 className="display-4 mb-3" style={{ fontFamily: 'Lora, serif', fontWeight: 'bold' }}>
            Medical Departments and Centers
          </h1>
          <p className="lead mb-4" style={{ fontSize: '1.1rem' }}>
            Comprehensive medical services across our specialized departments
          </p>

          <div className="row mt-4">
            <div className="col-lg-6">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="input-group input-group-lg">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search departments & centers"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ borderRadius: '4px 0 0 4px' }}
                  />
                  <button className="btn btn-light" type="submit" style={{ borderRadius: '0 4px 4px 0' }}>
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </form>
            </div>
            <div className="col-lg-6">
              <div>
                <p className="mb-2" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  Find departments & centers by first letter
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {alphabet.map((ltr) => (
                    <button
                      key={ltr}
                      type="button"
                      className={`btn ${letter === ltr ? 'btn-light' : 'btn-outline-light'}`}
                      onClick={() => handleLetterClick(ltr)}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        fontWeight: '600',
                        border: letter === ltr ? '2px solid #fff' : '2px solid rgba(255,255,255,0.5)'
                      }}
                    >
                      {ltr}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`btn ${letter === '#' ? 'btn-light' : 'btn-outline-light'}`}
                    onClick={() => handleLetterClick('#')}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      fontWeight: '600',
                      border: letter === '#' ? '2px solid #fff' : '2px solid rgba(255,255,255,0.5)'
                    }}
                  >
                    #
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section - White Background */}
      <section className="py-5" style={{ backgroundColor: '#fff', minHeight: '60vh' }}>
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : showSearchResults ? (
            <>
              <h2 className="mb-4">Search Results for "{searchQuery}"</h2>
              {searchResults.length === 0 ? (
                <p>No departments found matching your search.</p>
              ) : (
                <div className="row">
                  <div className="col-lg-9">
                    {Object.keys(groupedSearchResults).sort().map((key) => (
                      <div key={key} className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{key}</h3>
                        <ul className="list-unstyled">
                          {groupedSearchResults[key].map((dept) => (
                            <li key={dept.id} className="mb-2">
                              <Link to={`/departments/${dept.id}`} style={{ color: '#0066cc' }}>
                                {dept.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <a 
                          href="#top" 
                          onClick={(e) => {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          style={{ color: '#0066cc', fontSize: '0.9rem', textDecoration: 'none', cursor: 'pointer' }}
                        >
                          Back to top <i className="bi bi-arrow-up"></i>
                        </a>
                      </div>
                    ))}
                  </div>
                  <div className="col-lg-3">
                    <div className="border-start ps-4" style={{ borderColor: '#e0e0e0' }}>
                      <h6 className="mb-2">Appointments</h6>
                      <p className="text-muted small mb-3">
                        Contact us to schedule an appointment or learn more about our departments.
                      </p>
                      <Link
                        to="/contact"
                        className="btn btn-primary btn-sm"
                        style={{ textDecoration: 'none' }}
                      >
                        Contact us
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {letter && (
                <div className="row">
                  <div className="col-lg-9">
                    {departments.length === 0 ? (
                      <div className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{letter}</h3>
                        <p>No departments found starting with '{letter}'.</p>
                      </div>
                    ) : (
                      Object.keys(groupedDepartments).sort().map((key) => (
                        <div key={key} className="mb-5">
                          <h3 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{key}</h3>
                          <ul className="list-unstyled">
                            {groupedDepartments[key].map((dept) => (
                              <li key={dept.id} className="mb-2">
                                <Link to={`/departments/${dept.id}`} style={{ color: '#0066cc' }}>
                                  {dept.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                          <a 
                            href="#top" 
                            onClick={(e) => {
                              e.preventDefault();
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            style={{ color: '#0066cc', fontSize: '0.9rem', textDecoration: 'none', cursor: 'pointer' }}
                          >
                            Back to top <i className="bi bi-arrow-up"></i>
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="col-lg-3">
                    <div className="border-start ps-4" style={{ borderColor: '#e0e0e0' }}>
                      <h6 className="mb-2">Appointments</h6>
                      <p className="text-muted small mb-3">
                        Contact us to schedule an appointment or learn more about our departments.
                      </p>
                      <Link
                        to="/contact"
                        className="btn btn-primary btn-sm"
                        style={{ textDecoration: 'none' }}
                      >
                        Contact us
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}

export default DepartmentsCenters;
